import { api } from './http';
import type {
  EmbeddingHealth,
  EmbeddingStats,
  FullSearchResults,
  ReindexResult,
  SearchContactResult,
  SearchDiagnostics,
  SearchDocumentResult,
  SearchMatterResult,
  SearchResults,
  SearchSyncRun,
  SemanticResult,
  SimilarMatter,
} from './types';

export const globalSearch = (q: string, semantic?: boolean) =>
  api.get<SearchResults>('/search', { params: { q, ...(semantic ? { semantic: true } : {}) } }).then(r => r.data);

export const fullSearch = (q: string, params?: { tab?: string; limit?: number; offset?: number; include_archived?: boolean }) =>
  api.get<FullSearchResults>('/search/full', { params: { q, ...params } }).then(r => r.data);

interface InstantSearchResponse<T> {
  hits: T[];
  query: string;
  source: string;
}

export const instantGlobalSearch = async (q: string): Promise<SearchResults> => {
  const [matters, contacts, documents] = await Promise.all([
    api.get<InstantSearchResponse<SearchMatterResult>>('/instant-search/matters', { params: { q } }).then(r => r.data),
    api.get<InstantSearchResponse<SearchContactResult>>('/instant-search/contacts', { params: { q } }).then(r => r.data),
    api.get<InstantSearchResponse<SearchDocumentResult>>('/instant-search/documents', { params: { q } }).then(r => r.data),
  ]);
  return {
    matters: matters.hits,
    contacts: contacts.hits,
    documents: documents.hits,
    legal_references: [],
    semantic_results: [],
    semantic_enabled: false,
  };
};

export const getSearchDiagnostics = () =>
  api.get<SearchDiagnostics>('/instant-search/status').then(r => r.data);

export const triggerInstantSearchSync = () =>
  api.post<{ synced: Record<string, number> }>('/instant-search/sync').then(r => r.data);

export const getSearchSyncHistory = (limit = 10) =>
  api.get<SearchSyncRun[]>('/instant-search/history', { params: { limit } }).then(r => r.data);

export const getEmbeddingStats = () =>
  api.get<EmbeddingStats>('/embeddings/stats').then(r => r.data);

export const getEmbeddingHealth = () =>
  api.get<EmbeddingHealth>('/embeddings/health').then(r => r.data);

export const triggerReindex = (batchSize?: number) =>
  api.post<ReindexResult>('/embeddings/reindex', null, { params: batchSize ? { batch_size: batchSize } : {} }).then(r => r.data);

export const findSimilarContent = (contentType: string, sourceId: number, limit?: number, sameTypeOnly?: boolean) =>
  api.get<{ results: SemanticResult[] }>('/embeddings/similar', { params: { content_type: contentType, source_id: sourceId, ...(limit ? { limit } : {}), ...(sameTypeOnly ? { same_type_only: true } : {}) } }).then(r => r.data);

export const getSimilarMatters = (matterId: number, limit: number = 5) =>
  api.get<SimilarMatter[]>('/embeddings/similar-matters', {
    params: { matter_id: matterId, limit },
  }).then(r => r.data);
